import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View, Pressable, ScrollView } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import useResponsiveness from '@/hooks/useResponsiveness';
import { BORDER_RADIUS } from '@/constants/config';
import { useModalStore, useTaskTemplatesStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import TemplateCard from '@/components/Tasks/TemplateCard';
import useTranslation from '@/hooks/useTranslation';

const createStyles = (colors, isXS) => StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingVertical: isXS ? 24 : 40,
    paddingHorizontal: isXS ? 0 : 24,
  },
  content: {
    width: '100%',
    maxWidth: 700,
    alignItems: 'center',
  },
  heroSection: {
    alignItems: 'center',
    marginBottom: isXS ? 32 : 48,
  },
  iconContainer: {
    position: 'relative',
    marginBottom: 24,
  },
  mainIcon: {
    width: isXS ? 80 : 96,
    height: isXS ? 80 : 96,
    borderRadius: isXS ? 24 : 28,
    backgroundColor: `rgba(59, 130, 246, 0.12)`,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: `rgba(59, 130, 246, 0.2)`,
  },
  sparkleAccent: {
    position: 'absolute',
    top: -8,
    right: -8,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.solid_color_primary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: `rgba(251, 191, 36, 0.3)`,
  },
  clockAccent: {
    position: 'absolute',
    bottom: -4,
    left: -10,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.solid_color_primary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: `rgba(34, 197, 94, 0.3)`,
  },
  title: {
    textAlign: 'center',
    marginBottom: 12,
    fontSize: isXS ? 24 : 32,
    lineHeight: isXS ? 32 : 40,
    color: colors.text_color_primary,
  },
  subtitle: {
    textAlign: 'center',
    maxWidth: 500,
    lineHeight: 24,
    opacity: 0.8,
    marginBottom: 24,
  },
  ctaButtons: {
    flexDirection: isXS ? 'column' : 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: isXS ? 8 : 0,
  },
  ctaButton: {
    minWidth: isXS ? 200 : 180,
    height: isXS ? 48 : 44,
  },
  ctaButtonText: {
    fontSize: isXS ? 15 : 14,
  },
  featuresGrid: {
    width: '100%',
    gap: 12,
    marginBottom: isXS ? 32 : 48,
  },
  featureCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: isXS ? 14 : 16,
    borderRadius: BORDER_RADIUS.medium,
    backgroundColor: colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  featureCardHovered: {
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_tertiary,
  },
  featureIconWrapper: {
    width: 40,
    height: 40,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  featureContent: {
    flex: 1,
  },
  featureTitle: {
    marginBottom: 4,
  },
  featureDescription: {
    lineHeight: 20,
    opacity: 0.75,
  },
  templateSection: {
    width: '100%',
    marginBottom: isXS ? 32 : 40,
  },
  templateSectionTitle: {
    textAlign: 'center',
    marginBottom: 16,
    opacity: 0.6,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  templateGrid: {
    flexDirection: isXS ? 'column' : 'row',
    gap: 12,
  },
  examplesSection: {
    width: '100%',
    marginBottom: isXS ? 32 : 40,
  },
  examplesTitle: {
    textAlign: 'center',
    marginBottom: 16,
    opacity: 0.6,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  examplesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
  },
  examplePill: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: colors.solid_color_tertiary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  examplePillHovered: {
    backgroundColor: colors.solid_color_secondary,
    borderColor: colors.accent_color_primary,
  },
  exampleText: {
    fontSize: 13,
  },
  bottomCta: {
    alignItems: 'center',
    gap: 12,
  },
  ctaHint: {
    opacity: 0.5,
  },
});

function TasksEmptyState({ onBrowseTemplates = null }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM } = useResponsiveness();
  const { openModal } = useModalStore(state => state.actions);
  const requireAuth = useRequireAuth();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  const FEATURES = useMemo(() => [
    {
      icon: 'calendar-clock',
      iconColor: 'rgba(59, 130, 246, 1)',
      iconBg: 'rgba(59, 130, 246, 0.12)',
      title: t('emptyState.tasks.setItAndForgetIt'),
      description: t('emptyState.tasks.setItAndForgetItDesc'),
    },
    {
      icon: 'envelope',
      iconColor: 'rgba(34, 197, 94, 1)',
      iconBg: 'rgba(34, 197, 94, 0.12)',
      title: t('emptyState.tasks.resultsInYourInbox'),
      description: t('emptyState.tasks.resultsInYourInboxDesc'),
    },
    {
      icon: 'wand-magic-sparkles',
      iconColor: 'rgba(251, 191, 36, 1)',
      iconBg: 'rgba(251, 191, 36, 0.12)',
      title: t('emptyState.tasks.readyToUseTemplates'),
      description: t('emptyState.tasks.readyToUseTemplatesDesc'),
    },
  ], [t]);

  const EXAMPLE_TASKS = useMemo(() => [
    t('emptyState.tasks.dailyNewsBriefing'),
    t('emptyState.tasks.morningDevotional'),
    t('emptyState.tasks.stockMarketSummary'),
    t('emptyState.tasks.weeklyResearchDigest'),
    t('emptyState.tasks.industryNewsRoundup'),
    t('emptyState.tasks.dailyPrayerPrompts'),
  ], [t]);
  
  const templates = useTaskTemplatesStore(state => state.templates);
  const getFeaturedTemplates = useTaskTemplatesStore(state => state.getFeaturedTemplates);
  const featuredTemplates = useMemo(() => getFeaturedTemplates().slice(0, 3), [getFeaturedTemplates, templates]);

  const handleCreateTask = useCallback(() => {
    requireAuth(
      () => openModal('CreateTaskModal'),
      {
        source: 'tasks_empty_create_task',
        title: 'Sign in to create a Task',
        subtitle: 'Scheduled tasks run on your account so we know when (and for whom) to fire them.',
        icon: 'calendar-clock',
      },
    );
  }, [openModal, requireAuth]);

  const handleTemplatePress = useCallback((template) => {
    requireAuth(
      () => openModal('SetupTaskFromTemplateModal', { template }),
      {
        source: 'tasks_empty_template_press',
        title: `Sign in to set up ${template?.name || 'this task'}`,
        subtitle: 'Scheduled tasks run on your account so we know when (and for whom) to fire them.',
        icon: 'calendar-clock',
      },
    );
  }, [openModal, requireAuth]);

  const handleExamplePress = useCallback(() => {
    requireAuth(
      () => openModal('CreateTaskModal'),
      {
        source: 'tasks_empty_example',
        title: 'Sign in to create a Task',
        subtitle: 'Scheduled tasks run on your account so we know when (and for whom) to fire them.',
        icon: 'calendar-clock',
      },
    );
  }, [openModal, requireAuth]);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {/* Hero Section */}
        <View style={styles.heroSection}>
          <View style={styles.iconContainer}>
            <View style={styles.mainIcon}>
              <Icon 
                name="robot" 
                size={isXS ? 36 : 44} 
                color="rgba(59, 130, 246, 1)" 
              />
            </View>
            <View style={styles.sparkleAccent}>
              <Icon 
                name="sparkles" 
                size={16} 
                color="rgba(251, 191, 36, 1)"
                solid 
              />
            </View>
            <View style={styles.clockAccent}>
              <Icon 
                name="clock" 
                size={14} 
                color="rgba(34, 197, 94, 1)" 
              />
            </View>
          </View>
          
          <Text weight="bold" style={styles.title}>
            {t('emptyState.tasks.title')}
          </Text>
          
          <Text size="m" color="secondary" style={styles.subtitle}>
            {t('emptyState.tasks.subtitle')}
          </Text>

          <View style={styles.ctaButtons}>
            {typeof onBrowseTemplates === 'function' ? (
              <Button
                text={t('pages.tasks.browseTemplates') || 'Browse templates'}
                onPress={onBrowseTemplates}
                variant="secondary"
                size={isXS ? 'large' : 'medium'}
                prependIcon="grid-2"
                style={styles.ctaButton}
                textStyle={styles.ctaButtonText}
              />
            ) : null}
            <Button
              text={t('emptyState.tasks.createCustomTask')}
              onPress={handleCreateTask}
              variant="primary"
              size={isXS ? 'large' : 'medium'}
              prependIcon="plus"
              style={styles.ctaButton}
              textStyle={styles.ctaButtonText}
            />
          </View>
        </View>

        {/* Features Grid */}
        <View style={styles.featuresGrid}>
          {FEATURES.map((feature, index) => (
            <Pressable 
              key={index}
              style={({ hovered }) => [
                styles.featureCard,
                hovered && styles.featureCardHovered,
              ]}
              onPress={handleCreateTask}
            >
              <View style={[styles.featureIconWrapper, { backgroundColor: feature.iconBg }]}>
                <Icon 
                  name={feature.icon} 
                  size={20} 
                  color={feature.iconColor} 
                />
              </View>
              <View style={styles.featureContent}>
                <Text weight="medium" style={styles.featureTitle}>
                  {feature.title}
                </Text>
                <Text size="s" color="secondary" style={styles.featureDescription}>
                  {feature.description}
                </Text>
              </View>
            </Pressable>
          ))}
        </View>

        {/* Featured Templates */}
        {featuredTemplates.length > 0 && (
          <View style={styles.templateSection}>
            <Text size="xs" weight="medium" color="secondary" style={styles.templateSectionTitle}>
              {t('emptyState.tasks.popularTemplates')}
            </Text>
            <View style={styles.templateGrid}>
              {featuredTemplates.map(template => (
                <TemplateCard
                  key={template.id || template.slug}
                  template={template}
                  onPress={handleTemplatePress}
                  style={{ width: isXS ? '100%' : undefined, flex: isXS ? undefined : 1 }}
                />
              ))}
            </View>
          </View>
        )}

        {/* Example Tasks */}
        <View style={styles.examplesSection}>
          <Text size="xs" weight="medium" color="secondary" style={styles.examplesTitle}>
            {t('emptyState.tasks.taskIdeas')}
          </Text>
          <View style={styles.examplesGrid}>
            {EXAMPLE_TASKS.map((example, index) => (
              <Pressable
                key={index}
                style={({ hovered }) => [
                  styles.examplePill,
                  hovered && styles.examplePillHovered,
                ]}
                onPress={handleExamplePress}
              >
                <Text size="s" color="primary" style={styles.exampleText}>
                  {example}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>

        {/* Bottom CTA */}
        <View style={styles.bottomCta}>
          <Text size="xs" color="secondary" style={styles.ctaHint}>
            {t('emptyState.tasks.bottomHint')}
          </Text>
        </View>
      </View>
    </View>
  );
}

export default React.memo(TasksEmptyState);
